import { DataTableActionBarAction } from '@/components/data-table/data-table-action-bar'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useBulkSetTagadaVault } from '@/lib/api/subscriptions'
import { Shield, Loader2 } from 'lucide-react'
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface SetTagadaVaultBulkActionModalProps {
	subscriptionIds: string[]
	size?: 'sm' | 'md'
}

export const SetTagadaVaultBulkActionModal = ({
	subscriptionIds,
	size = 'sm',
}: SetTagadaVaultBulkActionModalProps) => {
	const { t } = useTranslation('subscriptions')
	const { mutate: setTagadaVaultMutation, isPending } = useBulkSetTagadaVault()
	const [isOpen, setIsOpen] = useState(false)
	const [useTagadaVault, setUseTagadaVault] = useState(true)

	const handleOpen = useCallback(() => {
		setUseTagadaVault(true)
		setIsOpen(true)
	}, [])

	const handleSubmit = useCallback(() => {
		setTagadaVaultMutation(
			{ subscriptionIds, useTagadaVault },
			{ onSettled: () => setIsOpen(false) },
		)
	}, [setTagadaVaultMutation, subscriptionIds, useTagadaVault])

	const handleOpenChange = (open: boolean) => {
		if (!open && !isPending) setIsOpen(false)
	}

	return (
		<>
			{size === 'sm' && (
				<DataTableActionBarAction
					tooltip={t('bulkActions.setTagadaVault.tooltip')}
					isPending={isPending}
					onClick={handleOpen}
				>
					<Shield className="mr-2 h-4 w-4" />
					{t('bulkActions.setTagadaVault.button')}
				</DataTableActionBarAction>
			)}
			{size === 'md' && (
				<Button variant="outline" size="sm" onClick={handleOpen}>
					<Shield className="mr-2 h-4 w-4" />
					{t('bulkActions.setTagadaVault.button')}
				</Button>
			)}
			<Dialog open={isOpen} onOpenChange={handleOpenChange}>
				<DialogContent className="sm:max-w-[420px]">
					<DialogHeader>
						<DialogTitle>{t('bulkActions.setTagadaVault.title')}</DialogTitle>
						<DialogDescription>
							{t('bulkActions.setTagadaVault.confirmMessage', { count: subscriptionIds.length })}
						</DialogDescription>
					</DialogHeader>
					<div className="flex items-center justify-between space-x-2 py-4">
						<Label htmlFor="use-tagada-vault">
							{t('bulkActions.setTagadaVault.switchLabel')}
						</Label>
						<Switch
							id="use-tagada-vault"
							checked={useTagadaVault}
							onCheckedChange={setUseTagadaVault}
						/>
					</div>
					<DialogFooter>
						<Button onClick={() => setIsOpen(false)} variant="outline">
							{t('bulkActions.setTagadaVault.cancel')}
						</Button>
						<Button onClick={handleSubmit} disabled={isPending}>
							{isPending && <Loader2 className="size-4 animate-spin mr-2" />}
							{t('bulkActions.setTagadaVault.update')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
